import React, { useState } from 'react';
import { Shield, Lock, Eye, FileText, AlertCircle, CheckCircle, Sparkles, Users, Database, Bell } from 'lucide-react';

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: 'Overview', icon: Sparkles },
    { id: 'collection', title: 'Data Collection', icon: Database },
    { id: 'usage', title: 'How We Use Data', icon: Eye },
    { id: 'security', title: 'Security', icon: Lock },
    { id: 'rights', title: 'Your Rights', icon: CheckCircle },
    { id: 'contact', title: 'Contact Us', icon: Bell }
  ];

  const content = {
    overview: {
      title: 'Privacy Policy Overview',
      text: 'Welcome to Smart Talk. We respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data and tell you about your privacy rights.',
      lastUpdated: 'Last Updated: November 17, 2025'
    },
    collection: {
      title: 'Information We Collect',
      items: [
        { title: 'Personal Information', desc: 'Name, email address, phone number when you register', icon: Users },
        { title: 'Usage Data', desc: 'How you interact with our app, features used, time spent', icon: Eye },
        { title: 'Device Information', desc: 'Device type, operating system, unique device identifiers', icon: Shield },
        { title: 'Communication Data', desc: 'Messages and content you share (encrypted end-to-end)', icon: Lock }
      ]
    },
    usage: {
      title: 'How We Use Your Information',
      items: [
        { text: 'To provide and maintain Smart Talk services', icon: Sparkles },
        { text: 'To notify you about changes to our service', icon: Bell },
        { text: 'To provide customer support', icon: Users },
        { text: 'To gather analysis to improve our service', icon: Database },
        { text: 'To monitor the usage of our service', icon: Eye },
        { text: 'To detect, prevent and address technical issues', icon: Shield }
      ]
    },
    security: {
      title: 'Data Security',
      text: 'We implement industry-standard security measures to protect your data. All communications are encrypted end-to-end. We use secure servers and regularly update our security protocols.',
      features: [
        { title: 'End-to-end encryption', icon: Lock },
        { title: 'Secure data storage', icon: Database },
        { title: 'Regular security audits', icon: Shield },
        { title: 'Two-factor authentication', icon: CheckCircle }
      ]
    },
    rights: {
      title: 'Your Privacy Rights',
      text: 'You have the right to:',
      items: [
        'Access your personal data',
        'Correct inaccurate data',
        'Request deletion of your data',
        'Object to processing of your data',
        'Request transfer of your data',
        'Withdraw consent at any time'
      ]
    },
    contact: {
      title: 'Contact Information',
      text: 'If you have any questions about this Privacy Policy, please contact us:',
      details: {
        email: 'smarttalk.connect@gmail.com',
        phone: '+91 9630630309',
        address: 'Smart Talk Inc., India'
      }
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', position: 'relative' }}>
      {/* Animated Background */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '300px',
          height: '300px',
          background: 'rgba(236, 72, 153, 0.3)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          animation: 'float 6s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '50%',
          right: '10%',
          width: '250px',
          height: '250px',
          background: 'rgba(139, 92, 246, 0.3)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          animation: 'float 8s ease-in-out infinite reverse'
        }}></div>
      </div>

      {/* Header */}
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.1)', 
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                padding: '16px',
                borderRadius: '16px',
                boxShadow: '0 10px 40px rgba(236, 72, 153, 0.4)'
              }}>
                <Shield style={{ width: '40px', height: '40px', color: 'white' }} />
              </div>
              <div>
                <h1 style={{ fontSize: '36px', fontWeight: '900', color: 'white', margin: 0 }}>Smart Talk</h1>
                <p style={{ color: '#fbbf24', fontWeight: '600', margin: 0 }}>Your Privacy Matters ✨</p>
              </div>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              padding: '12px 24px',
              borderRadius: '50px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{ width: '8px', height: '8px', background: '#10b981', borderRadius: '50%', animation: 'pulse 2s infinite' }}></div>
              <span style={{ color: 'white', fontWeight: '600' }}>Secure & Protected</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '48px 24px', position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px' }}>
          {/* Sidebar */}
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              position: 'sticky',
              top: '24px'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'white', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles style={{ width: '20px', height: '20px', color: '#fbbf24' }} />
                Sections
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px 20px',
                        borderRadius: '16px',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: isActive ? 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)' : 'transparent',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '14px',
                        transform: isActive ? 'scale(1.05)' : 'scale(1)',
                        boxShadow: isActive ? '0 8px 32px rgba(236, 72, 153, 0.4)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      <Icon style={{ width: '24px', height: '24px' }} />
                      {section.title}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div style={{ gridColumn: 'span 2' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              padding: '48px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              {activeSection === 'overview' && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                    padding: '20px',
                    borderRadius: '24px',
                    marginBottom: '24px'
                  }}>
                    <Sparkles style={{ width: '48px', height: '48px', color: 'white' }} />
                  </div>
                  <h2 style={{ fontSize: '48px', fontWeight: '900', color: 'white', marginBottom: '16px' }}>
                    {content.overview.title}
                  </h2>
                  <p style={{ color: '#fbbf24', fontWeight: '600', fontSize: '18px', marginBottom: '32px' }}>
                    {content.overview.lastUpdated}
                  </p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '20px', lineHeight: '1.8', maxWidth: '800px', margin: '0 auto 32px' }}>
                    {content.overview.text}
                  </p>
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                    borderLeft: '4px solid #ec4899',
                    padding: '32px',
                    borderRadius: '16px'
                  }}>
                    <p style={{ color: 'white', fontWeight: '700', fontSize: '18px', margin: 0 }}>
                      🎉 By using Smart Talk, you agree to the collection and use of information in accordance with this policy.
                    </p>
                  </div>
                </div>
              )}

              {activeSection === 'collection' && (
                <div>
                  <h2 style={{ fontSize: '48px', fontWeight: '900', color: 'white', marginBottom: '32px', textAlign: 'center' }}>
                    {content.collection.title}
                  </h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
                    {content.collection.items.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={index}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            padding: '32px',
                            borderRadius: '20px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05) translateY(-8px)';
                            e.currentTarget.style.boxShadow = '0 20px 60px rgba(236, 72, 153, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1) translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <Icon style={{ width: '48px', height: '48px', color: 'white', marginBottom: '16px' }} />
                          <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'white', marginBottom: '12px' }}>
                            {item.title}
                          </h3>
                          <p style={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.6' }}>{item.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeSection === 'usage' && (
                <div>
                  <h2 style={{ fontSize: '48px', fontWeight: '900', color: 'white', marginBottom: '32px', textAlign: 'center' }}>
                    {content.usage.title}
                  </h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                    {content.usage.items.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={index}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            padding: '24px',
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '12px', borderRadius: '12px' }}>
                            <Icon style={{ width: '24px', height: '24px', color: 'white' }} />
                          </div>
                          <p style={{ color: 'white', fontWeight: '600', margin: 0, flex: 1 }}>{item.text}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeSection === 'security' && (
                <div>
                  <h2 style={{ fontSize: '48px', fontWeight: '900', color: 'white', marginBottom: '24px', textAlign: 'center' }}>
                    {content.security.title}
                  </h2>
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '20px', lineHeight: '1.8', textAlign: 'center', marginBottom: '40px' }}>
                    {content.security.text}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
                    {content.security.features.map((feature, index) => {
                      const Icon = feature.icon;
                      const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];
                      return (
                        <div
                          key={index}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            padding: '32px',
                            borderRadius: '20px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = `0 20px 60px ${colors[index]}40`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{ background: colors[index], padding: '16px', borderRadius: '12px' }}>
                              <Icon style={{ width: '32px', height: '32px', color: 'white' }} />
                            </div>
                            <span style={{ color: 'white', fontWeight: '700', fontSize: '18px' }}>{feature.title}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeSection === 'rights' && (
                <div>
                  <h2 style={{ fontSize: '48px', fontWeight: '900', color: 'white', marginBottom: '16px', textAlign: 'center' }}>
                    {content.rights.title}
                  </h2>
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '20px', textAlign: 'center', marginBottom: '40px' }}>
                    {content.rights.text}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                    {content.rights.items.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          padding: '24px',
                          borderRadius: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(236, 72, 153, 0.3) 100%)';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <CheckCircle style={{ width: '32px', height: '32px', color: '#10b981' }} />
                        <p style={{ color: 'white', fontWeight: '700', fontSize: '18px', margin: 0 }}>{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeSection === 'contact' && (
                <div>
                  <h2 style={{ fontSize: '48px', fontWeight: '900', color: 'white', marginBottom: '16px', textAlign: 'center' }}>
                    {content.contact.title}
                  </h2>
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '20px', textAlign: 'center', marginBottom: '40px' }}>
                    {content.contact.text}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '600px', margin: '0 auto' }}>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      padding: '24px',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }}>
                      <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)', padding: '16px', borderRadius: '12px' }}>
                        <FileText style={{ width: '32px', height: '32px', color: 'white' }} />
                      </div>
                      <div>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0, fontSize: '14px' }}>Email</p>
                        <p style={{ color: 'white', fontWeight: '700', fontSize: '20px', margin: 0 }}>{content.contact.details.email}</p>
                      </div>
                    </div>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      padding: '24px',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }}>
                      <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '16px', borderRadius: '12px' }}>
                        <Bell style={{ width: '32px', height: '32px', color: 'white' }} />
                      </div>
                      <div>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0, fontSize: '14px' }}>Phone</p>
                        <p style={{ color: 'white', fontWeight: '700', fontSize: '20px', margin: 0 }}>{content.contact.details.phone}</p>
                      </div>
                    </div>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      padding: '24px',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }}>
                      <div style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)', padding: '16px', borderRadius: '12px' }}>
                        <Shield style={{ width: '32px', height: '32px', color: 'white' }} />
                      </div>
                      <div>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0, fontSize: '14px' }}>Address</p>
                        <p style={{ color: 'white', fontWeight: '700', fontSize: '20px', margin: 0 }}>{content.contact.details.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        background: 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '32px 24px',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10
      }}>
        <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '18px', margin: 0 }}>
          ✨ © 2025 Smart Talk. All rights reserved. Made with 💜
        </p>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}